import React, { useEffect } from "react"

import { Trans } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import { Box, Grid } from "@mui/material"
import { Snackbar } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import type { OpenAPIV3 } from "openapi-types"
import SwaggerUI from "swagger-ui-react"
import "swagger-ui-react/swagger-ui.css"

import { LoadingContainer } from "../../../components/core/LoadingContainer"
import { KonanAPI } from "../../../services/KonanAPI"
import { SidAPI } from "../../../services/SidAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { CustomTokenRefresh } from "../../../types/generated/authentication/CustomTokenRefresh"
import { setAuthTokens } from "../../Auth/Slice"
import { useGetAuthHeaderValue, useIsAccessTokenValid } from "../../Auth/helpers"

import styles from "../Integration.module.scss"

interface Request {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any
}

type ParamsType = {
  id: string
}

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setApiSpec: (spec: any) => void
}

/**
 * Component responsible for rendering Swagger UI
 * @param {void} setApiSpec state setter for the API spec
 * @returns {React.ReactElement}
 */
export function APIIntegration(props: Readonly<Props>): React.ReactElement {
  const { id: projectId } = useParams<ParamsType>()
  const { setApiSpec } = props

  const dispatch = useDispatch()
  const isAccessTokenValid = useIsAccessTokenValid()
  const authHeaderValue = useGetAuthHeaderValue()

  // Fetch project docs using project ID in route
  const { isLoading: isLoadingOpenApiData, data: response } = useQuery<AxiosResponse<OpenAPIV3.Document>, AxiosError>(
    ["project-docs", projectId],
    () => KonanAPI.fetchProjectDocs(projectId as string),
    {
      retry: false,
    },
  )

  const refreshTokenMutation = useMutation<AxiosResponse<CustomTokenRefresh>, AxiosError, string>(SidAPI.refreshToken)

  // Extract auth data using useSelector
  const refreshToken = useSelector((state: RootState) => state.auth.refreshToken)

  // injecting request headers and to pass it later to swagger UI
  const injectRequestHeaders = async (req: Request): Promise<Request> => {
    if (!isAccessTokenValid) {
      const refreshResponse = await refreshTokenMutation.mutateAsync(refreshToken as string)
      await dispatch(setAuthTokens({ access: refreshResponse.data.access, refresh: refreshToken as string }))
    }
    req.headers.Authorization = authHeaderValue
    return req
  }

  // update the Api spec state if there's a returned data
  useEffect(() => {
    if (response?.data) {
      setApiSpec(response?.data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response?.data])

  return (
    <React.Fragment>
      {isLoadingOpenApiData ? (
        <LoadingContainer />
      ) : (
        <React.Fragment>
          <Grid item xs={12}>
            <Snackbar severity="info" fullWidth id="snackbar-auth-restApi">
              <Trans
                i18nKey="integration:apiIntegration.notice"
                components={[<strong />, <strong />, <br />, <strong />]}
              />
            </Snackbar>
          </Grid>
          <Box mt={2} />
          <Grid container className={styles.swaggerContainer}>
            <Grid item xs={12}>
              <SwaggerUI spec={response?.data} requestInterceptor={injectRequestHeaders} />
            </Grid>
          </Grid>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}
